import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Panel } from "primereact/panel";
import CustomAxios from "../utils/CustomAxios";
import { getHubIdFromSubdomain, fetchAppConfigPublic } from "../utils/getAppConfig";
import { updatePWAConfig } from "../utils/updatePWAConfig";
import { cacheAppConfigForSWPublic } from "../utils/cacheAppConfigForSW";
import groceryIcon from "../Assets/prime/login/Grocery.svg";

import './login.css';
import { updateMobileno } from "../Redux/user";

export function Login() {
    const toast = useRef(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        mobileno: '',
    });
    const mobileno = useSelector(state => state.user.mobileno);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Allow only numbers (prevent letters or special characters)
        if (/^\d*$/.test(value)) {
            setFormData({ ...formData, [name]: value });
        }
    };

    const isDisabled = formData.mobileno.length !== 10; 
    
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate mobile number length
        if (formData.mobileno.length !== 10) {
            toast.current.show({ severity: 'error', summary: 'Invalid Number', detail: 'Mobile number must be exactly 10 digits', life: 3000 });
            return;
        }

        setLoading(true);
        try {
            // Extract subdomain from hostname
            // Examples: keev.localhost -> keev, keev.example.com -> keev, localhost -> ""
            const hostname = window.location.hostname;
            const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
            
            let subdomain = "";
            
            if (!isIP) {
                // Handle localhost pattern: subdomain.localhost
                if (hostname.endsWith('.localhost')) {
                    subdomain = hostname.replace('.localhost', '');
                }
                // Handle simple domain pattern: subdomain.domain.tld (exactly 3 parts)
                // This excludes complex domains like azurestaticapps.net
                else {
                    const parts = hostname.split(".");
                    // Only extract subdomain for simple 3-part domains (subdomain.domain.tld)
                    // and ensure first part is not 'www'
                    if (parts.length === 3 && parts[0].toLowerCase() !== 'www') {
                        subdomain = parts[0];
                    }
                    // For 2-part domains (domain.tld) or complex multi-part domains, no subdomain
                }
            }
            
            const brand = subdomain;
            
            // Always include subdomain in the request (empty string if no subdomain)
            const loginData = {
                ...formData,
                subdomain: subdomain || ""
            };
            
            const response = await CustomAxios.post(`login/`, loginData);
            const responseData = response.data;
            const data = Array.isArray(responseData) ? responseData[0] : responseData;

            if (data.message === "OTP sent to user") {
                localStorage.setItem('user_id', data.user_id);  
                localStorage.setItem('mobileno', formData.mobileno);  
                localStorage.setItem('brand', brand);
                dispatch(updateMobileno(formData.mobileno))
                
                navigate("/otpVerification", { state: { mobileno: formData.mobileno } }); 
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: data.message, life: 3000 });
            }
        } catch (error) {
            
            if (error.response) {
                const errorMessage = error.response.data?.message || "Invalid Credentials";
                toast.current.show({ severity: 'error', summary: 'Error', detail: errorMessage, life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Network Error', life: 3000 });
            }
        }
        setLoading(false);
    };
    
    useEffect(() => {
        const token = localStorage.getItem('authtoken');
        if (token) {
            navigate("/home/product_details");
        } else {
            localStorage.removeItem('mobileno');
        }
    }, []);

    // Effect to fetch hub and update icon on mount
    useEffect(() => {
        const fetchHubAndUpdateIcon = async () => {
            try {
                // Extract subdomain from hostname (same logic as in handleSubmit)
                const hostname = window.location.hostname;
                const isIP = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
                
                let subdomain = "";
                
                if (!isIP) {
                    // Handle localhost pattern: subdomain.localhost
                    if (hostname.endsWith('.localhost')) {
                        subdomain = hostname.replace('.localhost', '');
                    }
                    // Handle simple domain pattern: subdomain.domain.tld (exactly 3 parts)
                    else {
                        const parts = hostname.split(".");
                        if (parts.length === 3 && parts[0].toLowerCase() !== 'www') {
                            subdomain = parts[0];
                        }
                    }
                }
                
                if (subdomain) {
                    // Try to get hub ID from subdomain
                    const hubId = await getHubIdFromSubdomain(subdomain);
                    
                    if (hubId) {
                        // Fetch AppConfig for this hub
                        const appConfig = await fetchAppConfigPublic(hubId);
                        
                        if (appConfig) {
                            // Cache AppConfig in service worker cache for manifest generation
                            await cacheAppConfigForSWPublic(hubId, appConfig);
                            
                            // Temporarily set hub in localStorage for updatePWAConfig to work
                            const originalHub = localStorage.getItem('selected_hub');
                            localStorage.setItem('selected_hub', hubId.toString());
                            
                            // Update PWA config (favicon, icons, etc.)
                            await updatePWAConfig(false);
                            
                            // Force manifest refresh by updating the manifest link with a new cache buster
                            const manifestLink = document.querySelector('link[rel="manifest"]');
                            if (manifestLink) {
                                const newCacheBuster = `hub=${hubId}&t=${Date.now()}`;
                                manifestLink.setAttribute('href', `/manifest.json?${newCacheBuster}`);
                                
                                // Force browser to re-fetch manifest by creating a new link element
                                // This ensures the service worker intercepts the request
                                setTimeout(() => {
                                    const newManifestLink = document.createElement('link');
                                    newManifestLink.setAttribute('rel', 'manifest');
                                    newManifestLink.setAttribute('href', `/manifest.json?${newCacheBuster}`);
                                    document.head.removeChild(manifestLink);
                                    document.head.appendChild(newManifestLink);
                                    
                                    // Also try to fetch it directly to trigger service worker
                                    fetch(`/manifest.json?${newCacheBuster}`).catch(() => {});
                                }, 100);
                            }
                            
                            // Restore original hub if it existed, otherwise keep it for after login
                            if (originalHub) {
                                localStorage.setItem('selected_hub', originalHub);
                            }
                        }
                    }
                }
            } catch (error) {
                // Continue with default icon - don't block login page
            }
        };
        
        fetchHubAndUpdateIcon();
    }, []); 
      
    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit(event);
        }
    };
      
    return (
        <div className="login-container">
            <Toast ref={toast} />
            <div className="login-card">
                {/* Storefront Icon */}
                <div className="store-icon">
                    <img 
                        src={groceryIcon } 
                        alt="Welcome To KOODAI" 
                        className="login-icon-img"
                        onError={(e) => {
                            console.error("Icon failed to load. groceryIcon:", groceryIcon);
                            // Try direct path as last resort
                            e.target.src = "/Assets/prime/login/Grocery.svg";
                        }}
                    />
                </div>

                {/* Title */}
                <div className="login-title-container">
                <h1 className="login-title">Welcome To KoodAI</h1>
                
                {/* Subtitle */}
                <p className="login-subtitle">Sign up or login to manage your store.</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                        <label htmlFor="mobileno" className="form-label">Mobile Number</label>
                        <div className="input-wrapper">
                            <i className="pi pi-phone input-icon" />
                            <InputText
                                id="mobileno"
                                name="mobileno"
                                type="tel"
                                value={formData.mobileno}
                                maxLength={10}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                placeholder="Enter 10-digit mobile number"
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                className="mobile-input"
                                disabled={loading}
                            />
                        </div>
                        <p className="instruction-text">We'll send you an OTP to verify</p>
                    </div>

                    <Button
                        type="submit"
                        disabled={isDisabled || loading}
                        className="send-otp-button"
                        loading={loading}
                        icon="pi pi-send"
                        label="Send OTP"
                    />

                </form>
            </div>
        </div>
    );
}
